<template>
  <div class="eval-report">
    <!-- Header -->
    <div class="report-header-wrapper">
      <div class="report-header">
        <h2 class="report-title">{{ evaluation.name }}</h2>
        <p v-if="evaluation.description" class="report-desc">
          {{ evaluation.description }}
        </p>
      </div>
      <woot-button
        variant="clear"
        icon="arrow-left"
        color-scheme="secondary"
        @click="$emit('on-back')"
      >
        {{ $t('CAMPAIGN.EVALUATIONS.REPORT.BACK') }}
      </woot-button>
    </div>

    <!-- Period switcher -->
    <div class="period-switcher">
      <button
        v-for="p in periods"
        :key="p.value"
        class="period-btn"
        :class="{ 'is-active': groupBy === p.value }"
        @click="switchPeriod(p.value)"
      >
        {{ p.label }}
      </button>
    </div>

    <div v-if="isLoading" class="report-loading">
      <span class="report-spinner" />
    </div>
    <template v-else>
      <!-- Chart + Filters side by side -->
      <div class="report-top">
        <div class="chart-section">
          <bar-chart
            :collection="chartCollection"
            :stacked="true"
            class="eval-bar-chart"
          />
        </div>

        <aside class="report-filters">
          <div class="filter-item">
            <p class="filter-label">
              {{ $t('CAMPAIGN.EVALUATIONS.FILTERS.DATE') }}
            </p>
            <date-range-picker
              :confirm-text="
                $t('CAMPAIGN.EVALUATIONS.FILTERS.DATE_CONFIRM_BTN')
              "
              :placeholder="$t('CAMPAIGN.EVALUATIONS.FILTERS.DATE_PLACEHOLDER')"
              class="font_normal"
              :value="dateRange"
              :disabled-date="disableFilterDate"
              @change="handleDateChange"
            />
          </div>
          <div
            v-if="isConversationSpecific && agentList.length"
            class="filter-item"
          >
            <p class="filter-label">Agents:</p>
            <multiselect
              v-model="selectedAgents"
              :options="agentList"
              track-by="name"
              label="name"
              :multiple="true"
              :close-on-select="false"
              :clear-on-select="false"
              :hide-selected="false"
              placeholder="All agents"
              :max-height="200"
              class="agent-multiselect"
            />
          </div>
          <div
            v-if="isConversationSpecific && agentList.length"
            class="filter-item"
          >
            <p class="filter-label">Agent breakdown:</p>
            <ul class="agent-list">
              <li
                v-for="agent in agentList"
                :key="agent.name"
                class="agent-item"
              >
                <span class="agent-count">{{ agent.count }}</span>
                <span>- {{ agent.name }}</span>
              </li>
            </ul>
          </div>
        </aside>
      </div>

      <!-- Pivot table -->
      <eval-report-pivot-table
        :dates="pivotDates"
        :rows="pivotRows"
        @cell-click="onCellClick"
      />
    </template>

    <!-- Detail modal -->
    <eval-report-detail-modal
      v-if="showModal"
      :is-open="showModal"
      :title="modalTitle"
      :records="modalRecords"
      @close="showModal = false"
    />
  </div>
</template>

<script>
import moment from 'moment';
import helperMixin from 'shared/mixins/helperMixin';
import alertMixin from 'shared/mixins/alertMixin';
import Multiselect from 'vue-multiselect';
import DateRangePicker from 'components/ui/DateRangePicker.vue';
import BarChart from 'dashboard/components/widgets/chart/BarChart.js';
import { EVALUATION_TYPE } from '../evaluationConstant';
import EvaluationApi from 'dashboard/api/evaluations.js';
import EvalReportPivotTable from './EvalReportPivotTable.vue';
import EvalReportDetailModal from './EvalReportDetailModal.vue';

const PAGE_SIZE = 200;

const GROUP_FORMAT = {
  days: 'D/M',
  weeks: 'D/M',
  months: 'MMM YY',
};

export default {
  components: {
    Multiselect,
    DateRangePicker,
    BarChart,
    EvalReportPivotTable,
    EvalReportDetailModal,
  },
  mixins: [helperMixin, alertMixin],
  props: {
    evaluation: {
      type: Object,
      default: () => ({ name: '', description: '', kind: '' }),
    },
    evaluationId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      dateRange: this.defaultRangeForPeriod('days'),
      groupBy: 'days',
      isLoading: false,
      allRecords: [],
      settingKindMap: {},
      selectedAgents: [],
      showModal: false,
      modalTitle: '',
      modalRecords: [],
      chartColors: ['#4F46E5', '#0891B2', '#059669', '#D97706', '#DC2626'],
      periods: [
        { value: 'days', label: 'Days' },
        { value: 'weeks', label: 'Weeks' },
        { value: 'months', label: 'Months' },
      ],
    };
  },
  computed: {
    isConversationSpecific() {
      return this.evaluation.kind === EVALUATION_TYPE.CONVERSATION_SPEIFIC;
    },
    typeFilteredRecords() {
      if (this.evaluationId) {
        return this.allRecords.filter(
          r => r.evaluationable_id === this.evaluationId
        );
      }
      const kind = this.evaluation.kind;
      if (!kind || !Object.keys(this.settingKindMap).length) {
        return this.allRecords;
      }
      return this.allRecords.filter(
        r => this.settingKindMap[r.evaluationable_id] === kind
      );
    },
    agentList() {
      const agentMap = {};
      this.typeFilteredRecords.forEach(r => {
        const name = r.llm_chatbot?.name || 'Unknown';
        agentMap[name] = (agentMap[name] || 0) + 1;
      });
      return Object.entries(agentMap)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);
    },
    filteredRecords() {
      if (!this.selectedAgents.length) return this.typeFilteredRecords;
      const names = this.selectedAgents.map(a => a.name);
      return this.typeFilteredRecords.filter(r =>
        names.includes(r.llm_chatbot?.name || 'Unknown')
      );
    },
    groupKey() {
      return r => {
        const m = moment(r.created_at);
        if (this.groupBy === 'weeks') return m.startOf('isoWeek').format('D/M');
        return m.format(GROUP_FORMAT[this.groupBy] || 'D/M');
      };
    },
    pivotDates() {
      const seen = new Map();
      this.filteredRecords.forEach(r => {
        const key = this.groupKey(r);
        if (!seen.has(key)) {
          seen.set(key, moment(r.created_at));
        }
      });
      return Array.from(seen.entries())
        .sort((a, b) => a[1].valueOf() - b[1].valueOf())
        .map(([key]) => key);
    },
    resultTypes() {
      const types = new Set();
      this.filteredRecords.forEach(r => types.add(r.result));
      return Array.from(types);
    },
    pivotRows() {
      if (!this.filteredRecords.length) return [];
      const gk = this.groupKey;
      const totalValues = this.pivotDates.map(
        date => this.filteredRecords.filter(r => gk(r) === date).length
      );

      const rows = [
        { label: 'Total conversation', key: 'total', values: totalValues },
      ];

      this.resultTypes.forEach(result => {
        const values = this.pivotDates.map(
          date =>
            this.filteredRecords.filter(
              r => gk(r) === date && r.result === result
            ).length
        );
        rows.push({ label: result, key: result, values });
      });

      return rows;
    },
    chartSeries() {
      const gk = this.groupKey;
      return this.resultTypes.map(result => ({
        name: result,
        data: this.pivotDates.map(
          date =>
            this.filteredRecords.filter(
              r => gk(r) === date && r.result === result
            ).length
        ),
      }));
    },
    chartCollection() {
      return {
        labels: this.pivotDates,
        datasets: this.chartSeries.map((s, i) => ({
          label: s.name,
          data: s.data,
          backgroundColor: this.chartColors[i % this.chartColors.length],
        })),
      };
    },
  },
  mounted() {
    this._alive = true;
    if (!this.evaluationId) this.fetchSettingKindMap();
    this.fetchAllRecords();
  },
  beforeDestroy() {
    this._alive = false;
  },
  methods: {
    async fetchSettingKindMap() {
      try {
        const res = await EvaluationApi.getEvaluationsList();
        const map = {};
        (res.data.data || []).forEach(s => {
          map[s.id] = s.kind;
        });
        this.settingKindMap = map;
      } catch (error) {
        // silently ignore — filter will show all records as fallback
      }
    },
    async fetchAllRecords() {
      this.isLoading = true;
      this.allRecords = [];
      try {
        const { start_date, end_date } = this.formatStartEndDate(
          this.dateRange[0],
          this.dateRange[1]
        );

        const firstRes = await EvaluationApi.getEvaluationReports({
          page: 1,
          page_size: PAGE_SIZE,
          start_date,
          end_date,
        });

        const total = firstRes.data.meta?.count || 0;
        const totalPages = Math.ceil(total / PAGE_SIZE);
        let allData = [...(firstRes.data.data || [])];

        if (totalPages > 1) {
          const pagePromises = [];
          for (let p = 2; p <= totalPages; p++) {
            pagePromises.push(
              EvaluationApi.getEvaluationReports({
                page: p,
                page_size: PAGE_SIZE,
                start_date,
                end_date,
              })
            );
          }
          const results = await Promise.all(pagePromises);
          results.forEach(res => {
            allData = allData.concat(res.data.data || []);
          });
        }

        if (this._alive) this.allRecords = allData;
      } catch (error) {
        if (this._alive) this.showErrAlert(error, 'Something went wrong!');
      } finally {
        if (this._alive) this.isLoading = false;
      }
    },
    handleDateChange(dateRange) {
      this.dateRange = dateRange;
      this.selectedAgents = [];
      this.fetchAllRecords();
    },

    disableFilterDate(date) {
      const today = moment();
      const nMonthsAgo = moment().subtract(6, 'months');
      const targetDate = moment(date);
      return (
        targetDate.isAfter(today, 'day') ||
        targetDate.isBefore(nMonthsAgo, 'day')
      );
    },
    defaultRangeForPeriod(period) {
      const end = new Date();
      const start = new Date();
      if (period === 'days') start.setDate(start.getDate() - 6);
      else if (period === 'weeks') start.setDate(start.getDate() - 55);
      else start.setMonth(start.getMonth() - 5);
      return [start, end];
    },
    switchPeriod(period) {
      this.groupBy = period;
    },
    onCellClick({ rowKey, rowLabel, date }) {
      const gk = this.groupKey;
      this.modalRecords = this.filteredRecords.filter(
        r => gk(r) === date && r.result === rowKey
      );
      this.modalTitle = `${rowLabel} — ${date}`;
      this.showModal = true;
    },
  },
};
</script>

<style lang="scss" scoped>
.eval-report {
  padding: var(--space-normal);
}

.report-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

@keyframes report-spin {
  to {
    transform: rotate(360deg);
  }
}

.report-spinner {
  display: block;
  width: 48px;
  height: 48px;
  border: 4px solid var(--s-100);
  border-top-color: var(--w-500);
  border-radius: 50%;
  animation: report-spin 0.8s linear infinite;
}

.report-header {
  &-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: start;
  }

  margin-bottom: var(--space-large);

  .report-title {
    font-size: var(--font-size-big);
    font-weight: var(--font-weight-bold);
    color: var(--s-900);
    margin-bottom: var(--space-smaller);
  }

  .report-desc {
    font-size: var(--font-size-small);
    color: var(--s-500);
    margin: 0;
  }
}

.period-switcher {
  display: flex;
  gap: var(--space-smaller);
  margin-bottom: var(--space-normal);
}

.period-btn {
  padding: var(--space-smaller) var(--space-normal);
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  border: 1px solid var(--s-100);
  border-radius: var(--border-radius-normal);
  color: var(--s-600);
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: var(--w-300);
    color: var(--w-600);
  }

  &.is-active {
    background: var(--w-500);
    border-color: var(--w-500);
    color: var(--white);
  }
}

.report-top {
  display: flex;
  gap: var(--space-jumbo);
  margin-bottom: var(--space-large);
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: var(--space-large);
  }
}

.chart-section {
  flex: 1;
  min-width: 0;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 100%;
  }
}

.eval-bar-chart {
  height: 320px;
  width: 100%;
}

.report-filters {
  display: flex;
  flex-direction: column;
  gap: var(--space-large);
  width: 320px;
  flex-shrink: 0;

  .filter-label {
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-medium);
    color: var(--s-600);
    margin-bottom: var(--space-smaller);
  }

  .filter-item {
    min-width: 0;

    @media (min-width: 769px) {
      :deep(.date-picker),
      :deep(.mx-datepicker),
      :deep(.mx-datepicker-range),
      :deep(.mx-input-wrapper),
      :deep(.mx-input) {
        width: 100% !important;
        max-width: 100%;
      }
    }
  }

  @media (max-width: 768px) {
    width: 100%;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: var(--space-large);

    .filter-item {
      flex: 1;
      min-width: 240px;
    }
  }
}

.agent-multiselect {
  width: 100%;
}

.agent-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.agent-item {
  font-size: var(--font-size-small);
  color: var(--s-700);
  padding: var(--space-smaller) 0;
  display: flex;
  gap: var(--space-smaller);

  .agent-count {
    font-weight: var(--font-weight-bold);
  }
}
</style>

<style lang="scss">
.report-filters .date-picker .mx-datepicker-range {
  width: 100% !important;
}
</style>
